import 'ol/ol.css';
import './styles.css';

import Feature from 'ol/Feature.js';
import ImageState from 'ol/ImageState.js';
import Map from 'ol/Map.js';
import Point from 'ol/geom/Point.js';
import View from 'ol/View.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import ImageLayer from 'ol/layer/Image.js';
import VectorLayer from 'ol/layer/Vector.js';
import ImageWMS from 'ol/source/ImageWMS.js';
import VectorSource from 'ol/source/Vector.js';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style.js';
import { defaults as defaultInteractions } from 'ol/interaction/defaults.js';
import { fromLonLat, transformExtent } from 'ol/proj.js';
import { unByKey } from 'ol/Observable.js';

const PRODUCT_LAYER = 'mtg_fd:rgb_geocolour';
const MAP_ROI = transformExtent([-3.05, 37.23, 0.40, 40.37], 'EPSG:4326', 'EPSG:3857');
const MAP_LIMITS = transformExtent([-5.80, 34.80, 3.20, 42.80], 'EPSG:4326', 'EPSG:3857');
const HISTORY_HOURS = 3;
const FRAME_DELAY_MS = 400;
const END_HOLD_MS = 1_100;
const REFRESH_INTERVAL_MS = 60_000;
const FADE_DURATION_MS = 130;
const SOURCE_LOAD_TIMEOUT_MS = 22_000;
const SPEEDS = [0.5, 1, 2];
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const elements = Object.fromEntries([
  'closeInfo', 'fitHome', 'frameCount', 'frameSpinner', 'goLatest', 'infoDialog',
  'liveRegion', 'loadingCard', 'loadingDetail', 'loadingTitle', 'localTime',
  'map', 'nextFrame', 'openInfo', 'playbackSpeed', 'playLabel', 'playPause',
  'preloadState', 'previousFrame', 'rangeSummary', 'retryLoad', 'sourceStatus',
  'sourceStatusText', 'timeRange', 'timelineTicks', 'toast', 'toastAction',
  'toastMessage', 'utcTime', 'zoomIn', 'zoomOut',
].map((id) => [id, document.getElementById(id)]));

const localTimeFormatter = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Madrid',
  timeZoneName: 'short',
});

const accessibleTimeFormatter = new Intl.DateTimeFormat('es-ES', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Madrid',
  timeZoneName: 'short',
});

const utcTimeFormatter = new Intl.DateTimeFormat('es-ES', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'UTC',
});

const tickFormatter = new Intl.DateTimeFormat('es-ES', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'Europe/Madrid',
});

const state = {
  frames: [],
  manifest: null,
  displayedIndex: -1,
  requestedIndex: -1,
  frontLayer: 0,
  requestToken: 0,
  playing: false,
  playTimer: null,
  speedIndex: 1,
  bootstrapRunning: false,
  refreshTimer: null,
  freshnessTimer: null,
  toastTimer: null,
  sliderTimer: null,
  wasPlayingBeforeScrub: false,
  atHome: true,
  followLatest: true,
  preloadGeneration: 0,
  preloaded: new Set(),
};

const satelliteLayers = [0, 1].map(() => new ImageLayer({
  className: 'satellite-layer',
  opacity: 0,
  visible: false,
  zIndex: 1,
}));

const boundaryHalo = new Style({
  stroke: new Stroke({ color: 'rgba(2, 8, 14, 0.84)', width: 5.5 }),
});

const boundaryLine = new Style({
  fill: new Fill({ color: 'rgba(88, 199, 232, 0.025)' }),
  stroke: new Stroke({ color: 'rgba(255, 255, 255, 0.9)', width: 1.8 }),
});

const boundarySource = new VectorSource({
  url: '/api/boundaries?v=20260824',
  format: new GeoJSON({
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:3857',
  }),
});

const boundaryLayer = new VectorLayer({
  className: 'boundaries-layer',
  source: boundarySource,
  style: [boundaryHalo, boundaryLine],
  updateWhileAnimating: true,
  updateWhileInteracting: true,
  zIndex: 5,
});

const referencePlaces = [
  { name: 'Albacete', coordinates: [-1.85574745, 38.99588053], kind: 'capital' },
  { name: 'Alicante', coordinates: [-0.48318320, 38.34548705], kind: 'capital' },
  { name: 'Murcia', coordinates: [-1.12854080, 37.98436361], kind: 'capital' },
  { name: 'Valencia', coordinates: [-0.37565717, 39.47534441], kind: 'capital' },
  { name: 'Yecla', coordinates: [-1.11531939, 38.61341780], kind: 'reference' },
];

function markerStyle(radius) {
  return [
    new Style({
      image: new CircleStyle({
        radius,
        fill: new Fill({ color: 'rgba(2, 8, 14, 0.94)' }),
      }),
    }),
    new Style({
      image: new CircleStyle({
        radius: radius * 0.62,
        fill: new Fill({ color: '#f5f8fa' }),
      }),
    }),
    new Style({
      image: new CircleStyle({
        radius: radius * 0.34,
        fill: new Fill({ color: '#58c7e8' }),
      }),
    }),
  ];
}

const capitalMarkerStyle = markerStyle(8);
const referenceMarkerStyle = markerStyle(5.2);
const placeFeatures = referencePlaces.map(({ name, coordinates, kind }) => new Feature({
  geometry: new Point(fromLonLat(coordinates)),
  name,
  kind,
}));

const placeLayer = new VectorLayer({
  className: 'reference-places-layer',
  source: new VectorSource({ features: placeFeatures }),
  style: (feature) => (feature.get('kind') === 'capital' ? capitalMarkerStyle : referenceMarkerStyle),
  updateWhileAnimating: true,
  updateWhileInteracting: true,
  zIndex: 6,
});

const view = new View({
  projection: 'EPSG:3857',
  center: fromLonLat([-1.325, 38.80]),
  zoom: 6.8,
  minZoom: 5.5,
  maxZoom: 11.25,
  extent: MAP_LIMITS,
  enableRotation: false,
  smoothExtentConstraint: true,
});

const map = new Map({
  target: elements.map,
  layers: [...satelliteLayers, boundaryLayer, placeLayer],
  view,
  controls: [],
  interactions: defaultInteractions({
    altShiftDragRotate: false,
    keyboard: false,
    pinchRotate: false,
  }),
  moveTolerance: 4,
});

function makeSatelliteSource(time) {
  return new ImageWMS({
    url: '/api/wms',
    params: {
      LAYERS: PRODUCT_LAYER,
      FORMAT: 'image/jpeg',
      TIME: time,
      VERSION: '1.3.0',
    },
    ratio: 1.08,
    hidpi: false,
    interpolate: true,
  });
}

function setSourceBusy(busy) {
  elements.frameSpinner.classList.toggle('is-visible', busy);
  elements.timeRange.setAttribute('aria-busy', String(busy));
}

function waitForSource(source) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const keys = [];
    const timer = window.setTimeout(() => finish(new Error('La imagen tardó demasiado en responder.')), SOURCE_LOAD_TIMEOUT_MS);

    function finish(error) {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      keys.forEach(unByKey);
      if (error) reject(error);
      else resolve();
    }

    keys.push(source.once('imageloadend', () => finish()));
    keys.push(source.once('imageloaderror', () => finish(new Error('No se pudo cargar el fotograma.'))));
  });
}

function fadeLayers(oldLayer, newLayer, token) {
  if (reducedMotion.matches || !oldLayer.getVisible() || !oldLayer.getSource()) {
    oldLayer.setOpacity(0);
    newLayer.setOpacity(1);
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const startedAt = performance.now();
    function tick(now) {
      if (token !== state.requestToken) {
        resolve();
        return;
      }
      const progress = Math.min(1, (now - startedAt) / FADE_DURATION_MS);
      oldLayer.setOpacity(1 - progress);
      newLayer.setOpacity(progress);
      if (progress < 1) requestAnimationFrame(tick);
      else resolve();
    }
    requestAnimationFrame(tick);
  });
}

async function showFrame(index, { announce = false, force = false, quiet = false } = {}) {
  const safeIndex = Math.min(state.frames.length - 1, Math.max(0, Number(index)));
  if (!Number.isInteger(safeIndex) || !state.frames[safeIndex]) return false;
  if (!force && safeIndex === state.displayedIndex && state.requestedIndex < 0) return true;

  const token = ++state.requestToken;
  const time = state.frames[safeIndex];
  const nextLayerIndex = 1 - state.frontLayer;
  const nextLayer = satelliteLayers[nextLayerIndex];
  const oldLayer = satelliteLayers[state.frontLayer];
  const source = makeSatelliteSource(time);

  state.requestedIndex = safeIndex;
  setSourceBusy(true);
  const loaded = waitForSource(source);
  nextLayer.setOpacity(0.001);
  nextLayer.setSource(source);
  nextLayer.setVisible(true);
  map.render();

  try {
    await loaded;
    if (token !== state.requestToken) return false;
    nextLayer.setOpacity(0);
    await fadeLayers(oldLayer, nextLayer, token);
    if (token !== state.requestToken) return false;

    oldLayer.setVisible(false);
    oldLayer.setOpacity(0);
    oldLayer.setSource(null);
    nextLayer.setOpacity(1);
    nextLayer.setVisible(true);
    state.frontLayer = nextLayerIndex;
    state.displayedIndex = safeIndex;
    state.requestedIndex = -1;
    updateFrameUi(safeIndex);
    setSourceBusy(false);
    if (announce) elements.liveRegion.textContent = `Imagen de ${formatAccessibleTime(time)}`;
    return true;
  } catch (error) {
    if (token !== state.requestToken) return false;
    nextLayer.setVisible(false);
    nextLayer.setOpacity(0);
    nextLayer.setSource(null);
    state.requestedIndex = -1;
    setSourceBusy(false);
    if (state.displayedIndex >= 0) updateRangePosition(state.displayedIndex);
    if (!quiet) {
      showToast(`No se pudo cargar ${formatCompactTime(time)}. Se conserva la imagen anterior.`, {
        actionLabel: 'Reintentar',
        action: () => showFrame(safeIndex, { announce: true, force: true }),
      });
    }
    return false;
  }
}

function formatCompactTime(time) {
  return localTimeFormatter.format(new Date(time)).replace(',', ' ·');
}

function formatAccessibleTime(time) {
  return accessibleTimeFormatter.format(new Date(time));
}

function formatUtc(time) {
  return `${utcTimeFormatter.format(new Date(time))} UTC`;
}

function relativeObservation(time) {
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(time)) / 60_000));
  if (minutes < 2) return 'hace un minuto';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `hace ${hours} h ${remaining} min` : `hace ${hours} h`;
}

function updateRangePosition(index) {
  const maximum = Math.max(0, state.frames.length - 1);
  elements.timeRange.value = String(index);
  const progress = maximum ? (index / maximum) * 100 : 0;
  elements.timeRange.style.setProperty('--progress', `${progress}%`);
  const time = state.frames[index];
  if (time) elements.timeRange.setAttribute('aria-valuetext', formatAccessibleTime(time));
}

function updateFrameUi(index) {
  const time = state.frames[index];
  if (!time) return;
  elements.localTime.dateTime = time;
  elements.localTime.textContent = formatCompactTime(time);
  elements.utcTime.textContent = `${formatUtc(time)} · ${relativeObservation(time)}`;
  updateRangePosition(index);

  const isLatest = index === state.frames.length - 1;
  elements.goLatest.classList.toggle('is-latest', isLatest);
  elements.previousFrame.disabled = index <= 0;
  elements.nextFrame.disabled = index >= state.frames.length - 1;
}

function renderTicks() {
  elements.timelineTicks.replaceChildren();
  if (!state.frames.length) return;
  const tickCount = window.innerWidth < 520 ? 4 : 7;
  const indexes = new Set();
  for (let index = 0; index < tickCount; index += 1) {
    indexes.add(Math.round((index * (state.frames.length - 1)) / (tickCount - 1)));
  }
  for (const index of indexes) {
    const span = document.createElement('span');
    span.textContent = tickFormatter.format(new Date(state.frames[index]));
    elements.timelineTicks.append(span);
  }
}

function setControlsEnabled(enabled) {
  elements.playPause.disabled = !enabled;
  elements.goLatest.disabled = !enabled;
  elements.timeRange.disabled = !enabled;
  elements.playbackSpeed.disabled = !enabled;
  if (!enabled) {
    elements.previousFrame.disabled = true;
    elements.nextFrame.disabled = true;
  }
}

function applyManifest(manifest) {
  if (!Array.isArray(manifest.frames) || !manifest.frames.length) {
    throw new Error('EUMETSAT no ha publicado fotogramas utilizables.');
  }
  if (manifest.frames.some((time) => !Number.isFinite(Date.parse(time)))) {
    throw new Error('La fuente devolvió una secuencia temporal no válida.');
  }

  const previousTime = state.displayedIndex >= 0 ? state.frames[state.displayedIndex] : null;
  state.manifest = manifest;
  state.frames = manifest.frames;
  elements.timeRange.max = String(state.frames.length - 1);
  elements.frameCount.textContent = `${state.frames.length} fotogramas`;
  elements.rangeSummary.textContent = `Últimas ${manifest.hours || HISTORY_HOURS} horas`;
  renderTicks();
  setControlsEnabled(true);

  if (previousTime) {
    const newIndex = state.frames.indexOf(previousTime);
    state.displayedIndex = newIndex;
    if (newIndex >= 0) updateFrameUi(newIndex);
  }
  updateSourceFreshness();
}

async function loadManifest() {
  const response = await fetch(`/api/frames?hours=${HISTORY_HOURS}`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'No se pudo consultar la secuencia satelital.');
  }
  return payload;
}

function showLoading(title, detail, { error = false } = {}) {
  elements.loadingTitle.textContent = title;
  elements.loadingDetail.textContent = detail;
  elements.retryLoad.hidden = !error;
  elements.loadingCard.classList.remove('is-hidden');
  elements.loadingCard.setAttribute('aria-busy', String(!error));
}

function hideLoading() {
  elements.loadingCard.classList.add('is-hidden');
  elements.loadingCard.setAttribute('aria-busy', 'false');
  window.setTimeout(() => {
    if (elements.loadingCard.classList.contains('is-hidden')) elements.loadingCard.hidden = true;
  }, 220);
}

async function bootstrap() {
  if (state.bootstrapRunning) return;
  state.bootstrapRunning = true;
  stopPlayback();
  setControlsEnabled(false);
  elements.loadingCard.hidden = false;
  showLoading('Conectando con EUMETSAT…', 'Buscando la última observación disponible');

  try {
    const manifest = await loadManifest();
    applyManifest(manifest);
    state.followLatest = true;
    fitHome(false);
    showLoading('Cargando la última observación…', formatCompactTime(manifest.latest));
    const loaded = await showFrame(state.frames.length - 1, { force: true, quiet: true });
    if (!loaded) throw new Error('No se pudo cargar la última imagen satelital.');
    hideLoading();
    startPreloading();
  } catch (error) {
    setSourceState('error', 'Fuente no disponible');
    showLoading('Las imágenes no están disponibles', error.message || 'No se pudo completar la conexión.', { error: true });
  } finally {
    state.bootstrapRunning = false;
  }
}

async function refreshManifest() {
  if (state.bootstrapRunning || !navigator.onLine) return;
  try {
    const oldLatest = state.frames.at(-1);
    const oldDisplayedTime = state.frames[state.displayedIndex];
    const manifest = await loadManifest();
    applyManifest(manifest);
    const newLatest = state.frames.at(-1);

    if (state.followLatest) {
      const displayedTime = state.frames[state.displayedIndex];
      if (displayedTime !== newLatest) {
        const loaded = await showFrame(state.frames.length - 1, { force: true, quiet: true });
        if (loaded) startPreloading();
      } else if (newLatest !== oldLatest) {
        startPreloading();
      }
      return;
    }

    if (oldDisplayedTime && !state.frames.includes(oldDisplayedTime)) {
      await showFrame(0, { force: true });
    }
  } catch {
    if (state.frames.length) {
      setSourceState('warning', 'Fuente inestable');
    }
  }
}

function setSourceState(sourceState, text) {
  elements.sourceStatus.dataset.state = sourceState;
  elements.sourceStatusText.textContent = text;
}

function updateSourceFreshness() {
  if (!navigator.onLine) {
    setSourceState('offline', 'Sin conexión');
    return;
  }
  const latest = state.frames.at(-1) || state.manifest?.latest;
  if (!latest) {
    setSourceState('loading', 'Conectando…');
    return;
  }
  const minutes = Math.max(0, Math.floor((Date.now() - Date.parse(latest)) / 60_000));
  if (state.manifest?.sourceDegraded) {
    setSourceState('warning', 'Fuente inestable');
  } else if (minutes <= 60) {
    setSourceState('fresh', `Actualizado · ${minutes} min`);
  } else if (minutes <= 120) {
    setSourceState('warning', `Datos retrasados · ${minutes} min`);
  } else {
    setSourceState('warning', `Imagen antigua · ${Math.floor(minutes / 60)} h`);
  }
  if (state.displayedIndex >= 0) updateFrameUi(state.displayedIndex);
}

function updatePlayUi() {
  elements.playPause.setAttribute('aria-pressed', String(state.playing));
  elements.playPause.setAttribute('aria-label', state.playing ? 'Pausar animación' : 'Reproducir animación');
  elements.playLabel.textContent = state.playing ? 'Pausar' : 'Reproducir';
}

function stopPlayback() {
  state.playing = false;
  if (state.playTimer) window.clearTimeout(state.playTimer);
  state.playTimer = null;
  updatePlayUi();
}

function schedulePlayback(delay) {
  if (!state.playing) return;
  state.playTimer = window.setTimeout(playbackStep, delay);
}

async function playbackStep() {
  if (!state.playing || !state.frames.length) return;
  let nextIndex = state.displayedIndex + 1;
  let hold = 0;
  if (nextIndex >= state.frames.length) {
    nextIndex = 0;
    hold = END_HOLD_MS;
  }

  if (hold) {
    state.playTimer = window.setTimeout(async () => {
      if (!state.playing) return;
      const loaded = await showFrame(nextIndex);
      schedulePlayback(loaded ? FRAME_DELAY_MS / SPEEDS[state.speedIndex] : 120);
    }, hold);
    return;
  }

  const loaded = await showFrame(nextIndex);
  schedulePlayback(loaded ? FRAME_DELAY_MS / SPEEDS[state.speedIndex] : 120);
}

function startPlayback() {
  if (state.playing || !state.frames.length) return;
  state.followLatest = false;
  state.playing = true;
  updatePlayUi();
  playbackStep();
}

function togglePlayback() {
  if (state.playing) stopPlayback();
  else startPlayback();
}

function stepFrame(delta) {
  stopPlayback();
  const nextIndex = Math.min(state.frames.length - 1, Math.max(0, state.displayedIndex + delta));
  state.followLatest = nextIndex === state.frames.length - 1;
  showFrame(nextIndex, { announce: true });
}

function goToLatest() {
  stopPlayback();
  state.followLatest = true;
  showFrame(state.frames.length - 1, { announce: true });
}

function cycleSpeed() {
  state.speedIndex = (state.speedIndex + 1) % SPEEDS.length;
  const value = SPEEDS[state.speedIndex];
  elements.playbackSpeed.textContent = `${value.toLocaleString('es-ES')}×`;
  const spoken = value === 1 ? 'normal' : `${value.toLocaleString('es-ES')} por`;
  elements.playbackSpeed.setAttribute('aria-label', `Velocidad de reproducción: ${spoken}`);
}

function homePadding() {
  if (window.innerWidth <= 720) return [92, 26, 190, 26];
  return [92, 72, 174, 72];
}

function fitHome(animate = true) {
  state.atHome = true;
  view.fit(MAP_ROI, {
    padding: homePadding(),
    maxZoom: 8.35,
    duration: animate && !reducedMotion.matches ? 260 : 0,
  });
}

function changeZoom(delta) {
  const target = Math.min(view.getMaxZoom(), Math.max(view.getMinZoom(), (view.getZoom() || 7) + delta));
  state.atHome = false;
  state.preloadGeneration += 1;
  elements.preloadState.textContent = '';
  if (reducedMotion.matches) view.setZoom(target);
  else view.animate({ zoom: target, duration: 180 });
}

function viewSignature() {
  const size = map.getSize();
  if (!size) return null;
  const extent = view.calculateExtent(size).map((value) => Math.round(value / 25) * 25);
  return `${extent.join(',')}|${size.join('x')}|${(view.getResolution() || 0).toFixed(4)}`;
}

function preloadImage(time, generation, signature) {
  return new Promise((resolve) => {
    if (generation !== state.preloadGeneration || !state.atHome || !navigator.onLine) {
      resolve(false);
      return;
    }
    const size = map.getSize();
    const resolution = view.getResolution();
    if (!size || !resolution) {
      resolve(false);
      return;
    }

    const key = `${signature}|${time}`;
    if (state.preloaded.has(key)) {
      resolve(true);
      return;
    }

    const source = makeSatelliteSource(time);
    const image = source.getImage(view.calculateExtent(size), resolution, 1, view.getProjection());
    if (!image) {
      resolve(false);
      return;
    }

    let settled = false;
    const timer = window.setTimeout(() => finish(false), SOURCE_LOAD_TIMEOUT_MS);
    const onChange = () => {
      const imageState = image.getState();
      if (imageState === ImageState.LOADED) finish(true);
      else if (imageState === ImageState.ERROR || imageState === ImageState.EMPTY) finish(false);
    };

    function finish(success) {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      image.removeEventListener('change', onChange);
      if (success) state.preloaded.add(key);
      resolve(success);
    }

    image.addEventListener('change', onChange);
    image.load();
    onChange();
  });
}

async function startPreloading() {
  const generation = ++state.preloadGeneration;
  if (!state.atHome || !state.frames.length || !navigator.onLine) return;
  const signature = viewSignature();
  if (!signature) return;

  const order = state.frames.map((_, index) => index);
  let cursor = 0;
  let completed = 0;
  elements.preloadState.textContent = 'Preparando animación…';

  async function worker() {
    while (cursor < order.length && generation === state.preloadGeneration && state.atHome) {
      const index = order[cursor];
      cursor += 1;
      await preloadImage(state.frames[index], generation, signature);
      completed += 1;
      if (generation === state.preloadGeneration) {
        elements.preloadState.textContent = completed < order.length
          ? `Preparando ${completed}/${order.length}`
          : 'Animación preparada';
      }
    }
  }

  await Promise.all([worker(), worker()]);
}

function showToast(message, { actionLabel = '', action = null, persistent = false } = {}) {
  if (state.toastTimer) window.clearTimeout(state.toastTimer);
  elements.toastMessage.textContent = message;
  elements.toast.hidden = false;
  elements.toastAction.hidden = !action;
  elements.toastAction.textContent = actionLabel;
  elements.toastAction.onclick = action
    ? () => {
      hideToast();
      action();
    }
    : null;
  if (!persistent) state.toastTimer = window.setTimeout(hideToast, 5_500);
}

function hideToast() {
  if (state.toastTimer) window.clearTimeout(state.toastTimer);
  state.toastTimer = null;
  elements.toast.hidden = true;
  elements.toastAction.onclick = null;
}

function handleSliderInput() {
  stopPlayback();
  const index = Number(elements.timeRange.value);
  state.followLatest = index === state.frames.length - 1;
  const time = state.frames[index];
  if (time) elements.timeRange.setAttribute('aria-valuetext', formatAccessibleTime(time));
  if (state.sliderTimer) window.clearTimeout(state.sliderTimer);
  state.sliderTimer = window.setTimeout(() => showFrame(index), 75);
}

function handleSliderCommit() {
  if (state.sliderTimer) window.clearTimeout(state.sliderTimer);
  state.sliderTimer = null;
  const index = Number(elements.timeRange.value);
  state.followLatest = index === state.frames.length - 1;
  showFrame(index, { announce: true });
}

elements.playPause.addEventListener('click', togglePlayback);
elements.previousFrame.addEventListener('click', () => stepFrame(-1));
elements.nextFrame.addEventListener('click', () => stepFrame(1));
elements.goLatest.addEventListener('click', goToLatest);
elements.playbackSpeed.addEventListener('click', cycleSpeed);
elements.zoomIn.addEventListener('click', () => changeZoom(1));
elements.zoomOut.addEventListener('click', () => changeZoom(-1));
elements.fitHome.addEventListener('click', () => fitHome(true));
elements.retryLoad.addEventListener('click', bootstrap);

elements.timeRange.addEventListener('pointerdown', () => {
  state.wasPlayingBeforeScrub = state.playing;
  stopPlayback();
});
elements.timeRange.addEventListener('pointerup', () => {
  if (state.wasPlayingBeforeScrub) startPlayback();
  state.wasPlayingBeforeScrub = false;
});
elements.timeRange.addEventListener('input', handleSliderInput);
elements.timeRange.addEventListener('change', handleSliderCommit);

elements.openInfo.addEventListener('click', () => elements.infoDialog.showModal());
elements.closeInfo.addEventListener('click', () => elements.infoDialog.close());
elements.infoDialog.addEventListener('click', (event) => {
  if (event.target === elements.infoDialog) elements.infoDialog.close();
});

elements.map.addEventListener('pointerdown', () => {
  state.atHome = false;
  state.preloadGeneration += 1;
  elements.preloadState.textContent = '';
});
elements.map.addEventListener('wheel', () => {
  state.atHome = false;
  state.preloadGeneration += 1;
  elements.preloadState.textContent = '';
}, { passive: true });

map.on('moveend', () => {
  if (state.atHome) startPreloading();
});

document.addEventListener('keydown', (event) => {
  if (elements.infoDialog.open || event.altKey || event.ctrlKey || event.metaKey) return;
  const active = document.activeElement;
  const mapHasFocus = active === elements.map || active === document.body;
  if (!mapHasFocus) return;

  if (event.code === 'Space') {
    event.preventDefault();
    togglePlayback();
  } else if (event.key === 'ArrowLeft') {
    event.preventDefault();
    stepFrame(-1);
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    stepFrame(1);
  } else if (event.key === 'Home') {
    event.preventDefault();
    stopPlayback();
    state.followLatest = false;
    showFrame(0, { announce: true });
  } else if (event.key === 'End') {
    event.preventDefault();
    goToLatest();
  }
});

window.addEventListener('offline', () => {
  stopPlayback();
  updateSourceFreshness();
  showToast('Sin conexión · se conserva la última imagen cargada.', { persistent: true });
});

window.addEventListener('online', () => {
  hideToast();
  updateSourceFreshness();
  refreshManifest();
});

document.addEventListener('visibilitychange', () => {
  if (document.hidden) stopPlayback();
  else refreshManifest();
});

let resizeTimer = null;
window.addEventListener('resize', () => {
  if (resizeTimer) window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(() => {
    map.updateSize();
    renderTicks();
    if (state.atHome) fitHome(false);
  }, 120);
});

reducedMotion.addEventListener?.('change', () => stopPlayback());

boundarySource.on('featuresloaderror', () => {
  showToast('Los límites provinciales no se pudieron cargar.');
});

updatePlayUi();
fitHome(false);
bootstrap();

state.refreshTimer = window.setInterval(refreshManifest, REFRESH_INTERVAL_MS);
state.freshnessTimer = window.setInterval(updateSourceFreshness, 60_000);
